import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Sparkles, Check, RefreshCw, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CoachMessage({ message, suggestedAction, onClick }) {
  const [actionCompleted, setActionCompleted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [swapping, setSwapping] = useState(false);

  const handleLogDone = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/coach360/log-action`, {
        action: suggestedAction,
        completed: true,
        timestamp: new Date().toISOString()
      });
      setActionCompleted(true);
      setShowFeedback(true);
      toast.success('Great job! Action logged.');
    } catch (error) {
      toast.error('Failed to log action');
    }
  };

  const handleSwapSuggestion = async (e) => {
    e.stopPropagation();
    setSwapping(true);
    try {
      // In a real app, this would fetch a new suggestion
      toast.info('New suggestion coming...');
      setTimeout(() => {
        setSwapping(false);
        toast.success('Here\'s an alternative suggestion!');
      }, 1000);
    } catch (error) {
      setSwapping(false);
      toast.error('Failed to get new suggestion');
    }
  };

  const handleFeedback = async (helpful, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/coach360/feedback`, {
        action: suggestedAction,
        helpful,
        timestamp: new Date().toISOString()
      });
      setShowFeedback(false);
      toast.success(helpful ? 'Thanks for the feedback!' : 'We\'ll improve our suggestions.');
    } catch (error) {
      // Silent fail for feedback
      setShowFeedback(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <Card
        className="glass-card p-5 cursor-pointer hover:border-primary/30 transition-colors"
        onClick={onClick}
        data-testid="coach-message-card"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-primary to-secondary shrink-0">
            <Bot size={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-heading font-semibold text-sm">Coach360</h3>
              <Sparkles size={14} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {message || "Good morning! I've analyzed your vitals and have some personalized suggestions for today."}
            </p>
            
            {/* Suggested Action with feedback loop */}
            {suggestedAction && (
              <div className="mt-3 space-y-2">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                  actionCompleted 
                    ? 'bg-[#8BB89F]/20 text-[#8BB89F]' 
                    : 'bg-primary/10 text-primary'
                }`}>
                  {actionCompleted ? (
                    <>
                      <Check size={12} />
                      Completed!
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} />
                      {suggestedAction}
                    </>
                  )}
                </div>
                
                {/* Action buttons */}
                {!actionCompleted && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={handleLogDone}
                      className="h-7 px-3 rounded-full bg-[#8BB89F] hover:bg-[#8BB89F]/90 text-white text-xs"
                      data-testid="log-done-btn"
                    >
                      <Check size={12} className="mr-1" />
                      Log Done
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSwapSuggestion}
                      disabled={swapping}
                      className="h-7 px-3 rounded-full text-xs"
                      data-testid="swap-suggestion-btn"
                    >
                      <RefreshCw size={12} className={`mr-1 ${swapping ? 'animate-spin' : ''}`} />
                      Swap
                    </Button>
                  </div>
                )}

                {/* Feedback buttons */}
                <AnimatePresence>
                  {showFeedback && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center gap-2 pt-2"
                    >
                      <span className="text-xs text-muted-foreground">Was this helpful?</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleFeedback(true, e)}
                        className="h-6 w-6 p-0 rounded-full hover:bg-[#8BB89F]/20"
                        data-testid="feedback-helpful-btn"
                      >
                        <ThumbsUp size={14} className="text-[#8BB89F]" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleFeedback(false, e)}
                        className="h-6 w-6 p-0 rounded-full hover:bg-[#D54B48]/20"
                        data-testid="feedback-not-helpful-btn"
                      >
                        <ThumbsDown size={14} className="text-[#D54B48]" />
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Chat prompt */}
            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
              <MessageSquare size={12} />
              <span>Tap to chat with Coach360</span>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
